import { compact } from "lodash";
/**
 * Filters out falsy values from an array.
 *
 * This function is an alias for the `compact` function.
 *
 * @param array - The array to filter.
 * @returns A new array with all falsy values removed.
 */
export const filterTruthy = compact;
/**
 * Filters out `null` and `undefined` values from an array.
 *
 * @template T - The type of elements in the array.
 * @param {Array<T | null | undefined>} array - The array to filter.
 * @returns {T[]} A new array containing only the defined (non-null and non-undefined) elements.
 */
export function filterDefined(array) {
    return array.filter((element) => (element === undefined || element === null ? false : true));
}
/**
 * Finds the index of the item that maps to a minimum value, based on specified predicate function.
 *
 * @template T - The type of elements in the array.
 * @param {T[]} array - The array to search through.
 * @param {(value: T, index: number) => number} predicate - Predicate function that takes an element and its index and returns a number that the minimum index is based on.
 * @returns {number} The index of the element for which the predicate returns the smallest value. Returns -1 if the array is empty or if all elements are `undefined`.
 */
export function findMinIndex(array, predicate) {
    let minValue;
    let minIndex = -1;
    for (let index = 0; index < array.length; index++) {
        const value = array.at(index);
        if (value === undefined) {
            continue;
        }
        if (minValue === undefined) {
            minValue = predicate(value, index);
            minIndex = index;
            continue;
        }
        const newValue = predicate(value, index);
        if (newValue < minValue) {
            minValue = newValue;
            minIndex = index;
        }
    }
    return minIndex;
}
// Checks whether arr1 is a subsequence of arr2
export function isSubsequence(parentArr, subArr, parentSearchIndex = 0) {
    if (!subArr.length)
        return true;
    if (parentArr.length < subArr.length)
        return false;
    const parentStartIndex = parentArr.indexOf(subArr[0], parentSearchIndex);
    if (parentStartIndex === -1)
        return false;
    if (parentStartIndex + subArr.length > parentArr.length)
        return false;
    for (let subIndex = 1; subIndex < subArr.length; subIndex++) {
        const parentIndex = parentStartIndex + subIndex;
        if (parentArr[parentIndex] !== subArr[subIndex]) {
            return isSubsequence(parentArr, subArr, parentStartIndex + 1);
        }
    }
    return true;
}
/**
 * Returns the element at the specified index, wrapping around the array if the index is out of bounds.
 * This can be thought of as a "safe" modulo operation for array indices where elements can be accessed
 * by both positive and negative indices, of any magnitude.
 */
export function modulo(array, index) {
    // Nessisary to prevent divide by zero
    if (array.length === 0) {
        return undefined;
    }
    if (index >= 0) {
        return array[index % array.length];
    }
    // Handle negative indices, including the cases where the index magnitude is larger than the array length
    // For example, if the index is -10 on an array of 3 elements, it should return the last element
    const reverseIndex = array.length + (index % array.length);
    if (reverseIndex === array.length) {
        return array[0];
    }
    else {
        return array[reverseIndex];
    }
}
/**
 * Returns the sum of the values of an array that satisfy a predicate.
 */
export function sum(array, predicate) {
    let sum = 0;
    for (const value of array) {
        sum += predicate(value);
    }
    return sum;
}
/*
 * Returns number of elements in an array that satisfy a predicate.
 */
export function count(array, predicate) {
    let i = 0;
    for (const value of array) {
        if (predicate(value)) {
            i++;
        }
    }
    return i;
}
/**
 * Rotates an array by moving elements from the beginning to the end.
 *
 * @template T - The type of elements in the array.
 * @param {T[]} arr - The array to rotate.
 * @param {number} n - The number of positions to rotate. Positive values rotate left, negative values rotate right.
 * @returns {T[]} A new array with elements rotated by the specified number of positions.
 */
export function rotateArray(arr, n) {
    n = n % arr.length;
    return arr.slice(n, arr.length).concat(arr.slice(0, n));
}
/**
 * Maps over an array and filters out undefined values. Note that an array is always returned. If the input
 * is `undefined`, an empty array is returned. Note that `null` values are accepted as defined.
 *
 * @param array - The array to map over.
 * @param mapper - The mapping function.
 * @returns A new array with the mapped values.
 */
export function mapDefined(array, mapper) {
    if (array === undefined) {
        return [];
    }
    return array.reduce((acc, value) => {
        if (value !== undefined) {
            const mapped = mapper(value);
            if (mapped !== undefined) {
                acc.push(mapped);
            }
        }
        return acc;
    }, []);
}
export const Arrays = {
    modulo,
    count,
    sum,
    rotateArray,
    mapDefined,
};
//# sourceMappingURL=arrays.js.map